import { AppAnswerDoc, AppAssistApi } from "./AppAssistApi";
import { getCoreUtils, kCore, nz, StringOnlyOf } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppTaskApi - tasks as a first-class, interactive feature: the body the page sends to `/tasks`, the
// reading of what comes back, and the shape of one task row (in `index.json` and in the answer).
//
// ⚠ RULES ONLY - NO `XMLHttpRequest`, NO `window`. The Bridge suite imports this file to pin every
// field name, limit and id shape against `TaskRules` (`TaskContract.test.ts`), and a transport in
// here would pass under tsx and fail under tsc - the trap `AppConnect`/`AppConnectWatch` records.
//
// ==================================================================================================
// !! THE CONTRACT (owner decisions 2026-09-26)
// ==================================================================================================
//
// - The HUMAN task state lives in `tasks.json` beside `facts.json` in each person's folder in the
//   subscriber's own Dropbox. The service writes it ONLY through `/tasks`, on a human click.
// - Every task has a stable id: `t-` + 12 hex (an AI task: sha1 of person key, folded label and
//   source anchor), `u-` + 12 hex (a task the owner added), `r-` + the person key's digits (the
//   reply the owner owes).
// - A human decision wins over the AI's status. Snoozed hides a task from "open" until `until` -
//   ⚠ THE PAGE COMPARES `until` IN THE OWNER'S TIME ZONE; the service only carries the date.
// - `index.json` `tasks[]` rows carry `id`, `owner`, `due`, `state`, `until`, `source` and
//   `decidedIso` ONLY while the service runs with `WAC_TASKS=1`. ⭐ THE PRESENCE OF `state` ON A ROW
//   IS THE APP'S SIGNAL TO SHOW THE NEW TASK UI (`HasState`); without it the old view stands.

/** A task the owner types in themselves. */
export class AppTaskAdd {
    public Label: string = "";
    public Value: string = "";
    /** `YYYY-MM-DD` or "". */
    public Due: string = "";
    /** `me`, `them`, `both`, or "" (the service reads "" as `me` - the owner added it). */
    public Owner: string = "";
}

/** One task, as `index.json` and the `/tasks` answer carry it. */
export class AppTaskRow {
    public Id: string = "";
    /** `ai`, `you` or `reply`. */
    public Source: string = "";
    public Label: string = "";
    public Value: string = "";
    /** Who must act: `me` (the archive owner), `them`, `both`, or "" (not said). */
    public Owner: string = "";
    /** `YYYY-MM-DD` or "". */
    public Due: string = "";
    /** `open`, `snoozed`, `done` or `dismissed`. */
    public State: string = "";
    /** `YYYY-MM-DD`, only while snoozed. */
    public Until: string = "";
    /** When the owner decided (ISO-8601), or "" for a state nobody clicked. */
    public DecidedIso: string = "";
    /** The source message's UTC date and time, and its in-page anchor (`m-YYYYMMDDHHMM`). */
    public Date: string = "";
    public Time: string = "";
    public Anchor: string = "";
}

/** The service's answer to one click. */
export class AppTaskAnswer {
    public Ok: boolean = false;
    public Task: AppTaskRow | null = null;
    /** The sentence to show as-is when `Ok` is false. */
    public Why: string = "";
    /**
     * ⭐ SEVERAL TABS (2026-09-27): false = the task was ALREADY in the asked state (another tab got there
     * first) and nothing was written - no "Let <name> know?" card. ⚠ An older service sends no `changed`:
     * read as true, today's behaviour.
     */
    public Changed: boolean = true;
    /** ⭐ A refusal that clears on its own (`retry` seconds, 1-600; 2026-09-29 - the archive still being restored). 0 = final. */
    public RetrySec: number = 0;
}

export class AppTaskApi {
    public static readonly Route: string = "/tasks";

    /** The per-person file in the subscriber's Dropbox, and its schema. */
    public static readonly FileName: string = "tasks.json";
    public static readonly Schema: number = 1;

    /** ⛔ Must match `TaskRules` - pinned by `TaskContract.test.ts`. */
    public static readonly Actions: string[] = ["done", "reopen", "snooze", "dismiss", "add"];
    public static readonly States: string[] = ["open", "snoozed", "done", "dismissed"];
    /** ⭐ `reconnect` (2026-09-27): a derived "Reconnect with Anna?" suggestion (`q-`), see `ReconnectPrefix`. */
    public static readonly Sources: string[] = ["ai", "you", "reply", "reconnect"];
    public static readonly Owners: string[] = ["me", "them", "both"];
    public static readonly MaxLabel: number = 200;
    public static readonly MaxValue: number = 500;
    /** A task done or dismissed longer ago than this leaves `index.json`. */
    public static readonly RecentDays: number = 30;

    public static readonly AiPrefix: string = "t-";
    public static readonly UserPrefix: string = "u-";
    public static readonly ReplyPrefix: string = "r-";
    /**
     * ⭐ THE FOURTH SHAPE (2026-09-27): `q-` + the person key's digits - "Reconnect with Anna?", a contact
     * who has gone quiet against their own level (`AppActivityRules.IsQuiet`). Derived like `r-`, never
     * asked of a model; a decision counts only if taken at/after the last message (`TaskStateOverlay`).
     */
    public static readonly ReconnectPrefix: string = "q-";

    /** The id shapes. ⛔ Allow-lists: nothing else is ever a task id. */
    public static readonly AiIdPattern: RegExp = /^t-[0-9a-f]{12}$/;
    public static readonly UserIdPattern: RegExp = /^u-[0-9a-f]{12}$/;
    public static readonly ReplyIdPattern: RegExp = /^r-[0-9]{5,20}$/;
    public static readonly ReconnectIdPattern: RegExp = /^q-[0-9]{5,20}$/;

    /** The page's ready-made AI-help questions about tasks (see `AppAssistApi.Presets`). */
    public static readonly AssistPresets: string[] = ["What do I owe them?", "Remind them about what's open"];

    /**
     * Is this one of the three id shapes?
     * DOCUMENTED EXCEPTION: pure pattern test. ERROR-SUPPRESSED-JUSTIFIED: false IS the safe answer.
     */
    /**
     * ⭐ THE ONE `u-` ID MAKER (`UserIdPattern`: `u-` + 12 hex), for the page (`AppTaskBoard`) and the service
     * (`TaskStateIds`). ⚠ `SecureOnly` DIFFERS ON PURPOSE: the service passes true - it never mints from `Math.random`
     * (pinned by `IdGeneratorFailure.test.ts`); a throw lands in `Where`'s catch and answers "" (nothing added). The page
     * passes false - an id is a uniqueness key, not a secret, and a browser without `getRandomValues` must still add a
     * task. `Where` is the caller's diagnostic label.
     */
    public static NewUserId = (SecureOnly: boolean, Where: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppTaskApi.UserPrefix + kCore.RandomHex(6, SecureOnly);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Where);
            return "";
        }
    };

    public static IdOk = (Id: string): boolean => {
        const T: string = nz(Id);
        return AppTaskApi.AiIdPattern.test(T) === true || AppTaskApi.UserIdPattern.test(T) === true
            || AppTaskApi.ReplyIdPattern.test(T) === true || AppTaskApi.ReconnectIdPattern.test(T) === true;
    };

    /**
     * Is this a real calendar date written `YYYY-MM-DD`?
     * DOCUMENTED EXCEPTION: pure check. ERROR-SUPPRESSED-JUSTIFIED: false IS the safe answer on any doubt.
     */
    public static DateOk = (Text: string): boolean => {
        try {
            const T: string = nz(Text);
            if (/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(T) === false) { return false; }
            const Ms: number = Date.parse(T + "T00:00:00Z");
            if (isNaN(Ms) === true) { return false; }
            return new Date(Ms).toISOString().substring(0, 10) === T;
        } catch (e) {
            return false;
        }
    };

    /**
     * The body for one click, or "" when it cannot be sent.
     *
     * ⚠ `Page` is the href exactly as the index gave it; the service matches it against its own
     * index and never opens it. `Until` is sent only with `snooze`, `Add` only with `add`; for `add`
     * the page MAY pass its own `u-` id - a retried press then adds the task once, not twice.
     */
    public static Body = (Phone: string, Key: string, Page: string, Action: string, Id: string,
                          Until: string, Add: AppTaskAdd | null): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: Record<string, unknown> | null = AppTaskApi.ProofOf(Phone, Key, Page, AppTaskApi.Actions, Action, Id);
            if (Out === null) { return ""; }
            if (Action === "snooze") { Out.until = nz(Until).trim(); }
            if (Action === "add" && Add !== null) {
                Out.add = { label: nz(Add.Label).trim(), value: nz(Add.Value).trim(),
                    due: nz(Add.Due).trim(), owner: nz(Add.Owner).trim() };
            }
            return JSON.stringify(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskApi.Body");
            return "";
        }
    };

    /**
     * ⭐ THE PROVED-PRESS BODY `/tasks` AND `/money` SHARE (2026-10-14): `{number, key, page, action, id}`, or null when a
     * proof field is empty or `Action` is not one of `Allowed`. Each route adds its own fields after these.
     */
    public static ProofOf = (Phone: string, Key: string, Page: string, Allowed: string[], Action: string,
                             Id: string): Record<string, unknown> | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Allowed.indexOf(nz(Action)) < 0) { return null; }
            const Proof: Record<string, unknown> | null = AppAssistApi.ProofFields(Phone, Key, Page, Action);
            if (Proof === null) { return null; }
            Proof.id = nz(Id).trim();
            return Proof;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskApi.ProofOf");
            return null;
        }
    };

    /**
     * Read whatever came back.
     *
     * ⛔ THE BODY IS READ WHATEVER THE STATUS (`AppSignUpApi.Answer`'s rule): the service states its
     * refusals IN the body. ⚠ NO BOOLEAN `ok` IS A SERVICE WITHOUT THE ROUTE (`WAC_TASKS` unset
     * answers the plain status document) - `NotYetWhy`, never "at our end".
     */
    public static Parse = (Status: number, Text: string, OursWhy: string): AppTaskAnswer => {
        if (getCoreUtils().KErrorMode === true) { return AppTaskApi.No(OursWhy); }
        try {
            const Read = AppAssistApi.ReadAnswerDoc(Status, Text);
            const Doc: Record<string, unknown> | null = Read.Doc;
            if (Doc === null) { return AppTaskApi.No(OursWhy); }
            if (Read.HasOk === false) {
                const NotYet: AppTaskAnswer = AppTaskApi.No(AppTaskApi.NotYetWhy());
                return NotYet;
            }
            if (Doc.ok !== true) {
                const Refused: AppTaskAnswer = AppTaskApi.No(AppTaskApi.WhyOf(Doc.why, OursWhy));
                const RetrySec: number = AppAssistApi.RetryOf(Doc.retry);
                Refused.RetrySec = RetrySec;
                return Refused;
            }
            const Task: AppTaskRow | null = AppTaskApi.RowIn(Doc.task);
            if (Task === null) { return AppTaskApi.No(OursWhy); }
            const Out: AppTaskAnswer = Object.assign(new AppTaskAnswer(), {
                Ok: true, Task: Task, Changed: Doc.changed !== false,
            });
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unreadable body (a proxy's HTML error page) is our end,
            // and `OursWhy` says so on screen; reporting it would blank the page over a network blip.
            return AppTaskApi.No(OursWhy);
        }
    };

    /** The refusal's sentence: the body's `why` when it is a non-empty string, otherwise `Fallback`. */
    private static WhyOf = (Raw: unknown, Fallback: string): string => {
        const Text: string = nz(Raw as string);
        if (typeof Raw !== "string" || Text.length === 0) { return Fallback; }
        return Text;
    };

    /**
     * One task row (from `index.json` `tasks[]` or an answer's `task`), or null when it is not one.
     * ⚠ A row with no valid `id` or `state` is not a new-style task row - the caller keeps the old view.
     */
    public static RowIn = (Value: unknown): AppTaskRow | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Value === null || typeof Value !== "object" || Array.isArray(Value) === true) { return null; }
            const Bag: Record<string, unknown> = Value as Record<string, unknown>;
            // REUSE-JUSTIFIED: `AppIndexReader.TaskFields` copies some of the same keys, but into `AppRow` (another class,
            // other fields beside them) - one shared copier would need a key-to-property map assigned by string, longer
            // than the eight one-line copies it would replace and invisible to the type checker.
            const TextOf: (Raw: unknown) => string = AppTaskApi.Text;
            const IdOk: (Id: string) => boolean = AppTaskApi.IdOk;
            const States: string[] = AppTaskApi.States;
            const Out: AppTaskRow = Object.assign(new AppTaskRow(), {
                Id: TextOf(Bag.id), State: TextOf(Bag.state),
            });
            if (IdOk(Out.Id) === false || States.indexOf(Out.State) < 0) { return null; }
            Object.assign(Out, {
                Source: TextOf(Bag.source), Label: TextOf(Bag.label), Value: TextOf(Bag.value), Owner: TextOf(Bag.owner),
                Due: TextOf(Bag.due), Until: TextOf(Bag.until), DecidedIso: TextOf(Bag.decidedIso), Date: TextOf(Bag.date),
                Time: TextOf(Bag.time), Anchor: TextOf(Bag.anchor),
            });
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskApi.RowIn");
            return null;
        }
    };

    /**
     * ⭐ THE APP'S SWITCH: does this `index.json` task row carry the new fields? False = the service
     * runs with tasks off, and the page keeps the old task view.
     * DOCUMENTED EXCEPTION: pure check. ERROR-SUPPRESSED-JUSTIFIED: false (old view) IS the safe answer.
     */
    public static HasState = (Value: unknown): boolean => {
        if (Value === null || typeof Value !== "object" || Array.isArray(Value) === true) { return false; }
        return typeof (Value as Record<string, unknown>).state === "string";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static NotYetWhy = (): string => {
        return "Task updates aren't available yet.";
    };

    /** DOCUMENTED EXCEPTION: a field as text. ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    private static Text = (Raw: unknown): string => {
        return StringOnlyOf(Raw);
    };

    /** A refusal carrying `Why`. DOCUMENTED EXCEPTION: builds a value; cannot throw. */
    private static No = (Why: string): AppTaskAnswer => {
        const Out: AppTaskAnswer = new AppTaskAnswer();
        Out.Why = nz(Why);
        return Out;
    };
}
